import React, { useEffect } from 'react';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Siri Farms';
  }, []);

  return (
    <div className="bg-white">

      {/* Hero Section */}
      <section className="bg-[#3d5c28] py-16 flex items-center justify-center">
        <div className="text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Privacy Policy</h1>
          <p className="text-lg text-gray-200">Last Updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4 md:px-16 lg:px-24">
        <div className="max-w-4xl mx-auto space-y-10">
          {/* Section 1 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 1 – Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              When you purchase products from Siri Farms, create an account, enroll in a subscription,
              or contact us, we collect personal information you voluntarily provide, including but not
              limited to your name, shipping address, billing address, email address, phone number, and
              order details.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              When you browse our website, we automatically collect certain technical information such
              as your Internet Protocol (IP) address, browser type, device information, operating system,
              and usage data. This information helps us understand how visitors use our site and allows
              us to improve our services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We may also collect information through cookies, pixels, and similar technologies to
              support website functionality, analytics, and marketing.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 2 – How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We use your personal information for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>To process orders, payments, deliveries, pickups, and subscriptions</li>
              <li>To communicate with you about your orders, account, or customer service inquiries</li>
              <li>To send transactional messages such as order confirmations and delivery updates</li>
              <li>To send marketing communications by email or SMS if you have opted in</li>
              <li>To improve our website, products, and customer experience</li>
              <li>To comply with legal, regulatory, and agricultural requirements</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3 font-medium">
              We do not sell your personal information.
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 3 – Consent</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              When you provide personal information to complete a transaction, verify payment, place
              an order, arrange delivery or pickup, or enroll in a subscription, you consent to our
              collection and use of that information for those purposes.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              If we request your information for secondary purposes such as marketing, we will ask for
              your express consent or provide you with a clear opportunity to opt out.
            </p>
            <p className="text-gray-600 leading-relaxed">
              You may withdraw your consent at any time by contacting us using the information listed at
              the end of this policy.
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 4 – Disclosure of Information</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We may disclose your personal information if required to do so by law, regulation,
              subpoena, or court order, or if you violate our Terms of Service.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We may also share information with trusted third parties who assist us in operating our
              website, processing payments, shipping products, providing SMS services, or conducting
              business activities, provided they agree to keep this information confidential.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 5 – Third-Party Services</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Siri Farms uses third-party service providers for payment processing, shipping, analytics,
              SMS messaging, and social media integration.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              These providers collect, use, and disclose your information only to the extent necessary to
              perform the services they provide to us. Each provider has its own privacy practices, and
              we encourage you to review their privacy policies.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              Some third-party providers may be located in different jurisdictions. By using our services,
              you acknowledge that your information may be transferred to and processed in locations
              outside your state or country.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Once you leave our website or are redirected to a third-party site or application, you are no
              longer governed by this Privacy Policy.
            </p>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 6 – Payment Security</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Payment information is processed through secure payment gateways that comply with
              Payment Card Industry Data Security Standard (PCI-DSS) requirements.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We do not store full credit card numbers on our servers. All payment data is encrypted and
              handled according to industry security standards.
            </p>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 7 – Cookies and Tracking Technologies</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We use cookies and similar technologies to maintain website functionality, store user
              preferences, analyze traffic, and support marketing efforts.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Cookies may include session cookies, persistent cookies, and analytics cookies. You may
              choose to disable cookies through your browser settings, though some features of the
              website may not function properly.
            </p>
          </div>

          {/* Section 8 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 8 – SMS Text Messaging</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              By providing your phone number and opting in, you consent to receive SMS messages from
              Siri Farms, including order updates, delivery notifications, abandoned cart reminders, and
              promotional messages.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Message frequency may vary. Message and data rates may apply. You may opt out at any
              time by replying STOP to any message.
            </p>
          </div>

          {/* Section 9 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 9 – Email Marketing</h2>
            <p className="text-gray-600 leading-relaxed">
              If you opt in to email communications, we may send you updates about products,
              promotions, subscriptions, and company news. You may unsubscribe at any time using the
              link provided in our emails.
            </p>
          </div>

          {/* Section 10 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 10 – Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain personal information only as long as necessary to fulfill the purposes outlined in
              this policy, comply with legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </div>

          {/* Section 11 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 11 – Data Security</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We take reasonable precautions and follow industry best practices to protect your
              personal information from loss, misuse, unauthorized access, disclosure, alteration, or
              destruction.
            </p>
            <p className="text-gray-600 leading-relaxed">
              However, no method of transmission over the Internet or electronic storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </div>

          {/* Section 12 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 12 – Age Restrictions</h2>
            <p className="text-gray-600 leading-relaxed">
              By using this website, you represent that you are at least 18 years of age or the age of
              majority in your jurisdiction. We do not knowingly collect personal information from
              individuals under 18.
            </p>
          </div>

          {/* Section 13 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 13 – Changes to This Privacy Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We reserve the right to modify this Privacy Policy at any time. Changes will take effect
              immediately upon posting on the website.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              If we make material changes, we will update the &ldquo;Last Updated&rdquo; date at the top of this
              policy.
            </p>
            <p className="text-gray-600 leading-relaxed">
              If Siri Farms is acquired or merged with another company, your information may be
              transferred as part of that transaction.
            </p>
          </div>

          {/* Section 14 */}
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section 14 – Contact Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you would like to access, correct, amend, or delete any personal information we have
              about you, or if you have questions or concerns regarding this Privacy Policy, please
              contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="font-bold text-gray-800 text-lg">Siri Farms</p>
              <p className="text-gray-600">1800 NW 160th St</p>
              <p className="text-gray-600">Okeechobee, FL 34972</p>
              <p className="text-gray-600 mt-2">
                Email: <a href="mailto:sirifarmsfreshproduce@gmail.com" className="text-[#3d5c28] font-medium hover:underline">sirifarmsfreshproduce@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;